import React from "react";
import {
  Search,
  MapPin,
  Home,
  Hash,
  Building,
  Info,
  Lightbulb,
  WavesLadder,
  Warehouse,
  DollarSign,
} from "lucide-react";
import { AutocompleteSuggestion } from "../../../../api/locationAutocomplete";
import { useTenant } from "../../../../contexts/TenantContext";
import { useNavigate } from "react-router-dom";

interface SuggestionsDropdownProps {
  suggestions: AutocompleteSuggestion[];
  isLoading: boolean;
  selectedIndex: number;
  query: string;
  onSelect: (suggestion: AutocompleteSuggestion) => void;
  suggestionsRef: React.RefObject<HTMLDivElement>;
}

export const SuggestionsDropdown: React.FC<SuggestionsDropdownProps> = ({
  suggestions,
  isLoading,
  selectedIndex,
  query,
  onSelect,
  suggestionsRef,
}) => {
  const { config } = useTenant();
  const navigate = useNavigate();
  // Get icon based on suggestion type
  const getSuggestionIcon = (type: string) => {
    switch (type) {
      case "address":
        return <Home className="w-4 h-4 text-gray-500" />;
      case "city":
        return <Building className="w-4 h-4 text-gray-500" />;
      case "zip":
        return <Hash className="w-4 h-4 text-gray-500" />;
      case "neighborhood":
        return <MapPin className="w-4 h-4 text-gray-500" />;
      case "county":
        return <MapPin className="w-4 h-4 text-gray-500" />;
      case "natural_language":
        return <Info className="w-4 h-4 text-blue-500" />;
      case "listingid":
        return <Home className="w-4 h-4 text-gray-500" />;
      default:
        return <Search className="w-4 h-4 text-gray-500" />;
    }
  };

  // Highlight matching text in suggestions
  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;

    try {
      const regex = new RegExp(
        `(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
        "gi"
      );
      return text.replace(regex, "<strong>$1</strong>");
    } catch (e) {
      return text;
    }
  };

  return (
    <div
      ref={suggestionsRef}
      className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 sm:max-h-80 overflow-y-auto overflow-x-hidden left-0 right-0"
    >
      {isLoading && (
        <div className="p-3 sm:p-4 text-center text-gray-600 flex items-center justify-center">
          <span className="animate-pulse text-xl sm:text-2xl font-bold mx-0.5">
            .
          </span>
          <span className="animate-pulse delay-100 text-xl sm:text-2xl font-bold mx-0.5">
            .
          </span>
          <span className="animate-pulse delay-200 text-xl sm:text-2xl font-bold mx-0.5">
            .
          </span>
        </div>
      )}

      {/* Show natural language suggestions when there are no API suggestions but user has typed something */}
      {!isLoading && suggestions.length === 0 && query && query.length >= 2 && (
        <div className="p-2 sm:p-4">
          <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg mb-3 border border-blue-100">
            <Lightbulb className="w-5 h-5 text-blue-500 flex-shrink-0" />
            <div>
              <span className="font-bold text-blue-700">Tip:</span>{" "}
              <span className="text-sm text-blue-700">
                Use <strong>natural language</strong> to describe your ideal
                property. Our <strong>AI</strong> automatically matches your
                description to available properties.{" "}
                <span style={{ fontSize: "1.3em" }}>✨</span>
              </span>
            </div>
          </div>

          {[
            {
              text: "Modern 3 bedroom house",
              icon: (
                <Home
                  className="w-4 h-4 xl:w-5 xl:h-5"
                  style={{ color: "var(--primary)" }}
                />
              ),
            },
            {
              text: "Houses with garage",
              icon: (
                <Warehouse
                  className="w-4 h-4 xl:w-5 xl:h-5"
                  style={{ color: "var(--primary)" }}
                />
              ),
            },
            {
              text: "Single family home with pool",
              icon: (
                <WavesLadder
                  className="w-4 h-4 xl:w-5 xl:h-5"
                  style={{ color: "var(--primary)" }}
                />
              ),
            },
            {
              text: "Houses under $500,000",
              icon: (
                <DollarSign
                  className="w-4 h-4 xl:w-5 xl:h-5"
                  style={{ color: "var(--primary)" }}
                />
              ),
            },
          ].map((suggestion, index) => (
            <div
              key={`nl-suggestion-${index}`}
              className={`p-2.5 xl:p-3 flex items-center cursor-pointer hover:bg-gray-200 rounded-md ${
                index + suggestions.length === selectedIndex
                  ? "bg-gray-200"
                  : ""
              }`}
              onClick={() => {
                onSelect({
                  type: "natural_language",
                  value: suggestion.text,
                  display: suggestion.text,
                  metadata: {},
                });
              }}
            >
              {suggestion.icon}
              <div className="text-sm text-gray-800 font-medium ml-2">
                {suggestion.text}
              </div>
            </div>
          ))}
        </div>
      )}

      {suggestions.map((suggestion, index) => {
        // For address or listingid suggestions with listingkey, create a property details URL
        const isPropertyListing =
          (suggestion.type === "address" || suggestion.type === "listingid") &&
          suggestion.metadata.listingkey;
        const propertyUrl = isPropertyListing
          ? `/property/${suggestion.metadata.listingkey}?tenant_id=${
              config?.id || ""
            }&mls_osn=${suggestion.metadata.originatingsystemname || ""}`
          : null;

        // Handle click based on suggestion type
        const handleClick = () => {
          if (isPropertyListing && propertyUrl) {
            // Open property details in the same tab using navigate
            navigate(propertyUrl);
          } else {
            // Normal selection for non-property suggestions
            onSelect(suggestion);
          }
        };

        return (
          <div
            key={`${suggestion.type}-${suggestion.value}-${index}`}
            className={`p-2 md:p-4 flex flex-wrap sm:flex-nowrap items-center justify-center cursor-pointer hover:bg-gray-200 ${
              index === selectedIndex ? "bg-gray-200" : ""
            }`}
            onClick={handleClick}
          >
            {/* Show property image for address or listingid suggestions with image, otherwise show icon */}
            <div className="mr-2 sm:mr-3 flex-shrink-0">
              {(suggestion.type === "address" ||
                suggestion.type === "listingid") &&
              suggestion.metadata.first_image ? (
                <div className="w-10 h-10 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
                  <img
                    src={suggestion.metadata.first_image}
                    alt="Property"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback to icon if image fails to load
                      e.currentTarget.style.display = "none";
                      e.currentTarget.parentElement!.innerHTML =
                        '<div class="flex items-center justify-center w-full h-full"><svg class="w-5 h-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg></div>';
                    }}
                  />
                </div>
              ) : (
                getSuggestionIcon(suggestion.type)
              )}
            </div>

            <div className="flex-1 min-w-0">
              {/* For address or listingid suggestions */}
              {(suggestion.type === "address" ||
                suggestion.type === "listingid") &&
              suggestion.metadata.unparsedaddress ? (
                <div className="flex flex-col sm:flex-row sm:justify-between w-full">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-800 whitespace-nowrap overflow-hidden text-ellipsis w-full">
                      {`${suggestion.metadata.unparsedaddress}, ${[
                        suggestion.metadata.city,
                        suggestion.metadata.state,
                        suggestion.metadata.zip,
                      ]
                        .filter(Boolean)
                        .join(", ")}`}
                    </div>
                    {suggestion.metadata.listingid && (
                      <div className="text-xs text-gray-500 mt-0.5 whitespace-nowrap overflow-hidden text-ellipsis">
                        MLS# {suggestion.metadata.listingid}
                      </div>
                    )}
                    {/* Display type under the suggestion text on mobile only */}
                    <div className="text-xs text-gray-400 mt-0.5 uppercase whitespace-nowrap overflow-hidden text-ellipsis sm:hidden">
                      {suggestion.type}
                    </div>
                  </div>
                  {/* Type on right for desktop only */}
                  <div className="hidden sm:block text-xs uppercase text-gray-400 sm:mt-0 sm:ml-2 whitespace-nowrap overflow-hidden text-ellipsis">
                    {suggestion.type === "listingid" ? "MLS#" : suggestion.type}
                  </div>
                </div>
              ) : (
                /* For all other suggestion types */
                <div className="flex flex-col sm:flex-row sm:justify-between w-full">
                  <div className="flex-1">
                    <div
                      className="text-sm font-medium text-gray-800 whitespace-nowrap overflow-hidden text-ellipsis w-full"
                      dangerouslySetInnerHTML={{
                        __html: highlightMatch(suggestion.display, query || ""),
                      }}
                    />
                    {/* Display type under the suggestion text on mobile only */}
                    <div className="text-xs text-gray-400 mt-0.5 uppercase whitespace-nowrap overflow-hidden text-ellipsis sm:hidden">
                      {suggestion.type === "listingid"
                        ? "MLS#"
                        : suggestion.type}
                    </div>
                  </div>
                  {/* Type on right for desktop only */}
                  <div className="hidden sm:block text-xs uppercase text-gray-400 sm:mt-0 sm:ml-2 whitespace-nowrap overflow-hidden text-ellipsis">
                    {suggestion.type === "listingid" ? "MLS#" : suggestion.type}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SuggestionsDropdown;
